import * as THREE from 'three';
import { CHARACTERS, type CharacterDef } from './characters';
import { loadCharacterModel } from './load-model';

const STYLE_ID = 'cs-styles';

const CSS = `
.cs-root {
  position: relative; width: 100%; height: 100%; overflow: auto;
  background: radial-gradient(ellipse at 50% 30%, #1e150d, #0a0705);
  display: flex; align-items: center; justify-content: center;
  font-family: system-ui, sans-serif; color: #ecdcc0;
  padding: 24px; box-sizing: border-box;
}

/* ---- passport card ---- */
.cs-passport {
  display: flex; flex-direction: column; width: min(680px, 100%);
  background: linear-gradient(170deg, #2b1d13 0%, #1a120b 40%, #17100a 100%);
  border: 2px solid #5c4230; border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(210,164,86,0.1);
  overflow: hidden;
}

/* header strip */
.cs-header {
  background: linear-gradient(90deg, #3a2717, #2b1d13);
  border-bottom: 1px solid #5c4230;
  padding: 16px 24px; display: flex; align-items: center; gap: 12px;
}
.cs-header-icon {
  width: 28px; height: 28px; border-radius: 50%;
  background: radial-gradient(circle, #eac67e, #a07830);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; line-height: 1;
}
.cs-header-text {
  font: 700 20px/1 "Arial Narrow", system-ui, sans-serif;
  text-transform: uppercase; letter-spacing: 0.14em; color: #eac67e;
}
.cs-header-sub {
  font: 11px ui-monospace, monospace; color: #8a6e3e;
  letter-spacing: 0.08em; text-transform: uppercase; margin-left: auto;
}

/* body: two columns */
.cs-body { display: grid; grid-template-columns: 240px 1fr; gap: 0; }
@media (max-width: 600px) {
  .cs-body { grid-template-columns: 1fr; }
}

/* ---- left: character preview ---- */
.cs-left {
  display: flex; flex-direction: column; align-items: center;
  border-right: 1px solid #3a2a1a; padding: 20px 16px;
  background: linear-gradient(180deg, #1a120b, #0f0a06);
}
.cs-viewer {
  width: 100%; aspect-ratio: 3/4; border-radius: 8px; overflow: hidden;
  background: radial-gradient(120% 90% at 50% 20%, #2a1c12, #0e0906);
  border: 1px solid #3a2a1a;
}
.cs-viewer canvas { display: block; width: 100%; height: 100%; }
.cs-char-nav {
  display: flex; align-items: center; gap: 16px; margin-top: 14px;
}
.cs-nav-btn {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(180deg, #2b1d13, #17100a);
  border: 1px solid rgba(122,90,48,0.5); color: #d0a24e;
  font: 700 18px/1 monospace; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: border-color 0.12s, color 0.12s;
}
.cs-nav-btn:hover { border-color: #eac67e; color: #eac67e; }
.cs-char-label {
  font: 700 15px/1 "Arial Narrow", system-ui, sans-serif;
  text-transform: uppercase; letter-spacing: 0.08em; color: #eac67e;
  min-width: 80px; text-align: center;
}
.cs-char-desc {
  font: 12px/1.4 system-ui, sans-serif; color: #8a6e3e;
  text-align: center; margin-top: 6px; min-height: 2.8em;
}

/* ---- right: form ---- */
.cs-right { padding: 20px 24px; display: flex; flex-direction: column; gap: 18px; }

.cs-field-label {
  font: 700 10px/1 ui-monospace, monospace; text-transform: uppercase;
  letter-spacing: 0.12em; color: #8a6e3e; margin-bottom: 6px;
}
/* The dossier — read-only. The name input and the six-stat buy panel that
   used to live here are gone with the fields they wrote; see buildPassport.
   NO BACKTICKS in this block: the whole sheet is one template literal, and a
   backtick in a comment terminates it. */
.cs-dossier { display: flex; flex-direction: column; }
.cs-name-value {
  font: 18px/1.2 system-ui, sans-serif; color: #ecdcc0; letter-spacing: 0.02em;
  background: #120d09; border: 1px solid #3a2a1a; border-left: 3px solid #d0a24e;
  border-radius: 6px; padding: 11px 13px; word-break: break-word;
}
.cs-note {
  font: 11px/1.5 system-ui, sans-serif; color: #7b6240;
  margin: 7px 0 20px; max-width: 34ch;
}
.cs-dossier .cs-note:last-child { margin-bottom: 0; }

/* footer */
.cs-footer {
  border-top: 1px solid #3a2a1a; padding: 16px 24px;
  display: flex; justify-content: flex-end; align-items: center; gap: 12px;
  background: linear-gradient(180deg, #17100a, #120d09);
}
.cs-footer-error { color: #e08a72; }
.cs-footer-hint {
  font: 11px ui-monospace, monospace; color: #5c4a30;
  letter-spacing: 0.04em; margin-right: auto;
}
.cs-continue {
  font: 700 14px ui-monospace, monospace; text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #1a1208; background: linear-gradient(180deg, #eac67e, #c9a050);
  border: none; border-radius: 6px; padding: 12px 32px; cursor: pointer;
  transition: opacity 0.12s;
}
.cs-continue:disabled { opacity: 0.3; cursor: default; }
.cs-continue:not(:disabled):hover { opacity: 0.85; }

.cs-loading {
  font: 14px ui-monospace, monospace; color: #8a6e3e;
  letter-spacing: 0.06em; text-align: center; padding: 60px 0;
}
`;

/**
  * What the screen hands back: the preset the player signed for, and the model
  * already in memory so the caller does not refetch the GLB it just showed.
  *
  * No `playerName` and no `stats` — see the dossier column in `buildPassport`
  * for why those two fields are gone rather than unused.
  */
export interface CharacterSelectResult {
  character: CharacterDef;
  model: THREE.Group;
}

export function startCharacterSelect(
  root: HTMLElement,
  opts: {
    /** The account's username, shown as the survivor's name. Not editable here — the server does not take one. */
    playerName: string;
    /**
     * Called with the player's pick. May be async, and a REJECTION is
     * handled here: the screen stays up, the reason is shown in the footer
     * and the button re-arms. That is the screen's job rather than the
     * caller's because the caller has nowhere to draw — this module owns
     * `root` outright while it is mounted, so an error it does not render is
     * an error nobody sees.
     */
    onSelect: (result: CharacterSelectResult) => void | Promise<void>;
  },
): () => void {
  const { playerName, onSelect } = opts;
  let disposed = false;
  const cleanups: Array<() => void> = [];

  // ---- inject styles ----
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
    cleanups.push(() => style.remove());
  }

  root.innerHTML = '';
  const csRoot = document.createElement('div');
  csRoot.className = 'cs-root';
  root.appendChild(csRoot);

  // loading state
  const loadingEl = document.createElement('div');
  loadingEl.className = 'cs-loading';
  loadingEl.textContent = 'Loading characters…';
  csRoot.appendChild(loadingEl);

  // ---- state ----
  let charIndex = 0;

  const loadedModels = new Map<string, THREE.Group>();

  // ---- Three.js preview (single renderer) ----
  let previewRenderer: THREE.WebGLRenderer | null = null;
  let previewScene: THREE.Scene | null = null;
  let previewCamera: THREE.PerspectiveCamera | null = null;
  let previewModel: THREE.Group | null = null;

  // DOM refs for reactive updates
  let charLabelEl: HTMLElement;
  let charDescEl: HTMLElement;
  let bodyValueEl: HTMLElement;
  let hintEl: HTMLElement;
  let continueBtn: HTMLButtonElement;

  function setPreviewModel(charId: string) {
    const model = loadedModels.get(charId);
    if (!model || !previewScene) return;
    if (previewModel) previewScene.remove(previewModel);
    previewModel = model.clone();
    previewScene.add(previewModel);
  }

  function updateCharUI() {
    const char = CHARACTERS[charIndex];
    charLabelEl.textContent = char.name;
    charDescEl.textContent = char.description;
    bodyValueEl.textContent = char.name;
    setPreviewModel(char.id);
  }


  // ---- build passport DOM ----
  function buildPassport() {
    loadingEl.remove();

    const passport = document.createElement('div');
    passport.className = 'cs-passport';
    csRoot.appendChild(passport);

    // -- header --
    const header = document.createElement('div');
    header.className = 'cs-header';
    const icon = document.createElement('div');
    icon.className = 'cs-header-icon';
    icon.textContent = '✦';
    header.appendChild(icon);
    const headerText = document.createElement('div');
    headerText.className = 'cs-header-text';
    headerText.textContent = 'Survivor Registration';
    header.appendChild(headerText);
    const headerSub = document.createElement('div');
    headerSub.className = 'cs-header-sub';
    headerSub.textContent = 'Settlement Archives';
    header.appendChild(headerSub);
    passport.appendChild(header);

    // -- body --
    const body = document.createElement('div');
    body.className = 'cs-body';
    passport.appendChild(body);

    // LEFT: character preview
    const left = document.createElement('div');
    left.className = 'cs-left';
    body.appendChild(left);

    const viewer = document.createElement('div');
    viewer.className = 'cs-viewer';
    left.appendChild(viewer);

    const canvas = document.createElement('canvas');
    viewer.appendChild(canvas);

    // three.js setup
    previewScene = new THREE.Scene();
    previewScene.background = null;
    previewScene.add(new THREE.HemisphereLight(0x8fb6c4, 0x140d08, 0.7));
    const key = new THREE.DirectionalLight(0xffb27a, 1.2);
    key.position.set(3, 5, 4);
    previewScene.add(key);

    previewCamera = new THREE.PerspectiveCamera(35, 3 / 4, 0.1, 50);
    previewCamera.position.set(0, 1.0, 3.5);
    previewCamera.lookAt(0, 0.8, 0);

    previewRenderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'low-power',
    });
    previewRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    previewRenderer.setClearColor(0x000000, 0);

    // nav arrows + label
    const nav = document.createElement('div');
    nav.className = 'cs-char-nav';
    const prevBtn = document.createElement('button');
    prevBtn.className = 'cs-nav-btn';
    prevBtn.type = 'button';
    prevBtn.innerHTML = '&#9664;';
    prevBtn.addEventListener('click', () => {
      charIndex = (charIndex - 1 + CHARACTERS.length) % CHARACTERS.length;
      updateCharUI();
    });
    charLabelEl = document.createElement('div');
    charLabelEl.className = 'cs-char-label';
    const nextBtn = document.createElement('button');
    nextBtn.className = 'cs-nav-btn';
    nextBtn.type = 'button';
    nextBtn.innerHTML = '&#9654;';
    nextBtn.addEventListener('click', () => {
      charIndex = (charIndex + 1) % CHARACTERS.length;
      updateCharUI();
    });
    nav.append(prevBtn, charLabelEl, nextBtn);
    left.appendChild(nav);

    charDescEl = document.createElement('div');
    charDescEl.className = 'cs-char-desc';
    left.appendChild(charDescEl);

    // RIGHT: form
    const right = document.createElement('div');
    right.className = 'cs-right';
    body.appendChild(right);

    // The survivor's dossier — READ-ONLY, all of it.
    //
    // This column used to hold a "Survivor Name" input and a six-stat buy
    // panel with a pool of bonus points. `POST /character` accepts
    // `presetId` and `colorScheme` and nothing else, so both were discarded
    // the moment Continue was pressed: the name never reached the server
    // (the account's username IS the survivor's name) and the six stats were
    // not even the backend's five — it has STA where this had END, and no CHA
    // at all, and it grants the starting attributes itself with no free
    // points banked at creation.
    //
    // Letting a player spend six points and type a name that both evaporate
    // is worse than not asking, so the only choice this screen offers is the
    // one the endpoint actually takes: which body. Attributes are spent later,
    // through the Survivor Record's skill-points stage, against the real
    // five. See `createCharacterRow` in `state/game-state.ts`.
    const dossier = document.createElement('div');
    dossier.className = 'cs-dossier';
    right.appendChild(dossier);

    const nameLabel = document.createElement('div');
    nameLabel.className = 'cs-field-label';
    nameLabel.textContent = 'Survivor Name';
    dossier.appendChild(nameLabel);
    const nameValue = document.createElement('div');
    nameValue.className = 'cs-name-value';
    nameValue.textContent = playerName;
    dossier.appendChild(nameValue);
    const nameNote = document.createElement('div');
    nameNote.className = 'cs-note';
    nameNote.textContent = 'Your account name. The settlement knows you by it.';
    dossier.appendChild(nameNote);

    const bodyLabel = document.createElement('div');
    bodyLabel.className = 'cs-field-label';
    bodyLabel.textContent = 'Build';
    dossier.appendChild(bodyLabel);
    bodyValueEl = document.createElement('div');
    bodyValueEl.className = 'cs-name-value';
    dossier.appendChild(bodyValueEl);
    const bodyNote = document.createElement('div');
    bodyNote.className = 'cs-note';
    bodyNote.textContent = 'Locked once you sign. Everything else about you is still to be earned — '
      + 'attributes are yours to spend as you level, in the Survivor Record.';
    dossier.appendChild(bodyNote);

    // -- footer --
    const footer = document.createElement('div');
    footer.className = 'cs-footer';
    hintEl = document.createElement('div');
    hintEl.className = 'cs-footer-hint';
    hintEl.textContent = 'Your build is locked once you sign';
    const hint = hintEl;
    footer.appendChild(hint);
    continueBtn = document.createElement('button');
    continueBtn.className = 'cs-continue';
    continueBtn.type = 'button';
    continueBtn.textContent = 'Sign and enter';
    // Enabled from the start. It used to be disabled until a name of two or
    // more characters had been typed, which is the only reason it had a
    // disabled state at all; every preset is a valid answer, and the models
    // are all loaded before this DOM is built.
    continueBtn.addEventListener('click', () => {
      const char = CHARACTERS[charIndex];
      const model = loadedModels.get(char.id);
      if (!model) return;
      // Guards the double-click: creating the row is a network round trip and
      // the caller tears this screen down only once it answers, so a second
      // press would post twice. The loser of that race gets a 409, which
      // `createCharacterRow` already treats as success — but the request is
      // still avoidable.
      continueBtn.disabled = true;
      continueBtn.textContent = 'Signing…';
      hintEl.textContent = 'Filing your registration…';
      hintEl.classList.remove('cs-footer-error');
      void Promise.resolve(onSelect({ character: char, model: model.clone() }))
        .catch((err: unknown) => {
          if (disposed) return;
          // The screen stays. Dropping the player into a settlement with no
          // survivor in it would be the worse failure, and their pick is
          // still on screen and still valid to retry.
          hintEl.textContent = err instanceof Error
            ? err.message
            : 'Could not register you. Try again.';
          hintEl.classList.add('cs-footer-error');
          continueBtn.disabled = false;
          continueBtn.textContent = 'Try again';
        });
    });
    footer.appendChild(continueBtn);
    passport.appendChild(footer);

    // initial paint
    updateCharUI();

    // resize preview
    function resizePreview() {
      if (!previewRenderer) return;
      const parent = previewRenderer.domElement.parentElement;
      if (!parent) return;
      const w = parent.clientWidth || 1;
      const h = parent.clientHeight || 1;
      previewRenderer.setSize(w, h, false);
      if (previewCamera) {
        previewCamera.aspect = w / h;
        previewCamera.updateProjectionMatrix();
      }
    }
    const ro = new ResizeObserver(resizePreview);
    ro.observe(viewer);
    cleanups.push(() => ro.disconnect());
    resizePreview();
  }

  // ---- load all models then build UI ----
  Promise.all(
    CHARACTERS.map((char) =>
      loadCharacterModel(char.modelUrl).then((model) => {
        loadedModels.set(char.id, model);
      }),
    ),
  )
    .then(() => {
      if (disposed) return;
      buildPassport();
    })
    .catch((err) => {
      if (disposed) return;
      loadingEl.textContent = 'Failed to load character models.';
      console.error('Character load error:', err);
    });

  // ---- render loop ----
  let raf = 0;
  const renderLoop = (now: number): void => {
    raf = requestAnimationFrame(renderLoop);
    if (!previewRenderer || !previewScene || !previewCamera) return;
    if (previewModel) previewModel.rotation.y = now * 0.0005;
    previewRenderer.render(previewScene, previewCamera);
  };
  raf = requestAnimationFrame(renderLoop);

  // ---- teardown ----
  return () => {
    disposed = true;
    cancelAnimationFrame(raf);
    for (const fn of cleanups) fn();
    if (previewRenderer) {
      previewRenderer.dispose();
      previewRenderer.forceContextLoss();
    }
    if (previewScene) {
      previewScene.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (!m) return;
        const mats = Array.isArray(m) ? m : [m];
        for (const mat of mats) {
          const std = mat as THREE.MeshStandardMaterial;
          std.map?.dispose();
          std.normalMap?.dispose();
          std.roughnessMap?.dispose();
          std.metalnessMap?.dispose();
          mat.dispose();
        }
      });
    }
    csRoot.remove();
  };
}
